import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Grid2, Input, LinearProgress, Stack, useTheme } from '@mui/material';
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import axios from 'axios';
import _ from 'lodash';
import moment from "moment";
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMenus } from '../common/dialog.util';
import { hiddenInputSx } from '../common/styleUtil';
import { lighter } from '../common/util';
import { DialogsContext } from '../contexts';
import ScoreAvatar from '../enrollment/score.avatar';
import { studentApi } from './student.queries';

export default function StudentCard(props) {
	const navigate = useNavigate();
	const useGet = studentApi.useGet(props.id, {enabled: !props.student});
	const student = props.student || useGet?.data;

	const preUpload = studentApi.usePreUpload();
	const patch = studentApi.usePatch();

	const theme = useTheme();
	const menus = useMenus();
	const { dialogs } = useContext(DialogsContext);

	if (!student?.name) {
		return;
	}

	const uploadFile = async (file) => {
		const id = student.id;
		const data = {
			fileName: file.name,
			fileType: file.type,
		};
		let result = await preUpload.mutateAsync({id, data});

		const url = result.url;
		const { origin, pathname } = new URL(url);

		result = await axios.put(url, file, {
			headers: {
				'Content-Type': file.type,
			},
		});

		const updated = { id, profilePicture: origin + pathname };
		patch.mutateAsync(updated);
	};

	const href = "/students/" + student.id;
	const linkActive = window.location.pathname != href;

	let ageLabel = moment().diff(student.birthDate, 'years');
	ageLabel = ageLabel ? ageLabel + ' years old' : '';

	let completeness = 0;
	let score = 0;
	let activeEnrollmentCount = 0;
	let upcomingEnrollmentCount = 0;
	if (student?.enrollments?.length) {
		let enrollments = _.filter(student?.enrollments, 'score'); //Don't count zeros
		if (enrollments.length) {
			score = _.sumBy(enrollments, 'score') / enrollments.length;
		}

		let activeEnrollments = _.filter(student?.enrollments, e => e.completeness < 100 && (e.startDate < new Date().getTime() && e.endDate > new Date().getTime()));
		if( activeEnrollments.length ){
			completeness = _.sumBy(activeEnrollments, 'completeness') / activeEnrollments.length;
		}

		activeEnrollmentCount = activeEnrollments.length;
		upcomingEnrollmentCount = _.filter(student?.enrollments, e => e.completeness < 100 && e.startDate > new Date().getTime()).length;
	}

	const progressSx = {
		flexGrow: 1,
		height: 12,
		'.MuiLinearProgress-bar1Buffer':{backgroundColor:lighter(student.colour, 0.2, theme)},
		'.MuiLinearProgress-bar2Buffer':{backgroundColor:lighter(student.colour, 0.6, theme)},
	};

	const optionsMenu = (
		<Box>
			<IconButton className="hover-muted" onClick={(e) => menus.open('student', e)} sx={{ mr: -3 }}>
				<MoreVertIcon />
			</IconButton>
			<Menu open={menus.isOpen.student == true} onClose={() => menus.close()} anchorEl={menus.anchors.student}>
				<MenuItem onClick={() => { menus.close(); dialogs.open('student', student) }}>Edit</MenuItem>
				<MenuItem component="label" role={undefined}>Upload Avatar
					<Input sx={hiddenInputSx} type="file" onChange={(event) => { menus.close(); uploadFile(event.target.files[0]) }} />
				</MenuItem>
			</Menu>
		</Box>
	);

	const cardMediaSx = { height: '100%', aspectRatio: '1 / 1' };
	const cardMedia = (
		<CardMedia sx={cardMediaSx} component="img" src={student.profilePicture || '/missing-student.png'} />
	);

	const cardContent = (
		<CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
			<Stack spacing={1}>
				<Box sx={{ flex: 1 }}>
					<Typography variant="h5">{student.name}</Typography>
					{ageLabel ? <Typography variant="h6">{ageLabel}</Typography> : ''}
					{activeEnrollmentCount ? <Typography variant="subtitle1">{activeEnrollmentCount} Active Courses</Typography> : ''}{upcomingEnrollmentCount ? <Typography variant="subtitle1">{upcomingEnrollmentCount} Upcoming Courses</Typography> : ''}
					
				</Box>
				<ScoreAvatar score={score} size={32} />
				{!linkActive && !props.hideMenu && optionsMenu}
			</Stack>

			<Box sx={{ flexGrow: 1 }}></Box>
			{completeness > 0 &&
				<Box sx={{ ml: -6, mr: -5 }}>
					<Typography variant="subtitle2" sx={{ml: (completeness - 3) + '%'}}>{completeness.toFixed(0)}%</Typography>
					<LinearProgress title={completeness.toFixed(0) + '% Complete'} variant="buffer" value={completeness} valueBuffer={100} sx={progressSx} color="info" />
				</Box>
			}
		</CardContent>
	);

	let cardDetail = (
		<Grid2 container sx={{ width: '100%' }}>
			<Grid2 size={4}>
				{cardMedia}
			</Grid2>
			<Grid2 size={8} sx={{ display: 'flex' }}>
				{cardContent}
			</Grid2>
		</Grid2>
	);

	if (linkActive) {
		cardDetail = (
			<CardActionArea onClick={()=>navigate(href)} sx={{ display: 'flex', textDecoration: 'none', height: '100%' }}>
				{cardDetail}
			</CardActionArea>
		);
	}

	const cardSx = { width: '100%', background: lighter(student.colour, 0.8, theme) };
	return (
		<Card sx={cardSx} className="hover-toggle">
			{cardDetail}
		</Card>
	);
}